import type { ReactNode } from 'react'

export type RichTextHandlers = {
  onMention?: (handle: string) => void
  onHashtag?: (tag: string) => void
}

const ENTITY =
  /(@[a-zA-Z0-9_]{2,30})|(#[a-zA-Z][\w]{1,49})|(https?:\/\/[^\s<>"'`]+)|(www\.[^\s<>"'`]+)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g

const stripTrailingPunct = (value: string) => value.replace(/[),.!?;:]+$/g, '')

/**
 * Renders post/comment text with polished blue entities:
 * @mentions, #hashtags, URLs, and emails.
 */
export function renderRichText(text: string, handlers: RichTextHandlers = {}): ReactNode {
  if (!text) return null
  const nodes: ReactNode[] = []
  let last = 0
  let match: RegExpExecArray | null
  const re = new RegExp(ENTITY.source, 'g')
  let key = 0

  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(<span key={`t-${key++}`}>{text.slice(last, match.index)}</span>)
    }
    const raw = match[0]
    const clean = stripTrailingPunct(raw)
    const trailing = raw.slice(clean.length)

    if (match[1]) {
      const handle = clean.slice(1)
      nodes.push(
        <button
          key={`m-${key++}`}
          type="button"
          className="entity-link entity-mention"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            handlers.onMention?.(handle)
          }}
        >
          @{handle}
        </button>,
      )
    } else if (match[2]) {
      const tag = clean.slice(1)
      nodes.push(
        <button
          key={`h-${key++}`}
          type="button"
          className="entity-link entity-hashtag"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            handlers.onHashtag?.(tag)
          }}
        >
          #{tag}
        </button>,
      )
    } else if (match[3] || match[4]) {
      const href = clean.startsWith('http') ? clean : `https://${clean}`
      nodes.push(
        <a
          key={`u-${key++}`}
          className="entity-link entity-url"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
        >
          {clean.replace(/^https?:\/\//, '')}
        </a>,
      )
    } else if (match[5]) {
      nodes.push(
        <a
          key={`e-${key++}`}
          className="entity-link entity-email"
          href={`mailto:${clean}`}
          onClick={(event) => event.stopPropagation()}
        >
          {clean}
        </a>,
      )
    } else {
      nodes.push(<span key={`f-${key++}`}>{clean}</span>)
    }
    if (trailing) nodes.push(<span key={`p-${key++}`}>{trailing}</span>)
    last = match.index + raw.length
  }

  if (last < text.length) nodes.push(<span key={`t-${key++}`}>{text.slice(last)}</span>)
  return nodes
}
